'use client';

import type {
  MysteryRoomNpcActorKind,
  MysteryRoomNpcActorPreset,
  MysteryRoomNpcBehaviorProfile,
  MysteryRoomNpcFaction,
  MysteryRoomNpcRole,
} from '../world/mapTypes';

type NpcBehaviorSource = {
  id?: string;
  role: MysteryRoomNpcRole;
  actorKind?: MysteryRoomNpcActorKind;
  actorPreset?: MysteryRoomNpcActorPreset | string;
  faction?: MysteryRoomNpcFaction;
  behaviorProfile?: MysteryRoomNpcBehaviorProfile;
  awarenessState?: 'patrol' | 'suspicious' | 'alert' | 'search';
  alive?: boolean;
};

export type MysteryRoomNpcRelationshipToHero = 'ally' | 'neutral' | 'enemy';
export type MysteryRoomNpcBehaviorTreeNodeId =
  | 'dead'
  | 'hostile_target'
  | 'medical_support'
  | 'repair_support'
  | 'security_response'
  | 'world_alarm'
  | 'routine_patrol'
  | 'routine_work'
  | 'routine_rest'
  | 'fallback_wander';

export function resolveNpcBehaviorProfile(npc: NpcBehaviorSource): MysteryRoomNpcBehaviorProfile {
  if (npc.behaviorProfile) {
    return npc.behaviorProfile;
  }
  if (npc.actorPreset === 'zombie') {
    return 'zombie';
  }
  if (npc.actorKind === 'boss' || npc.actorPreset === 'boss') {
    return 'boss';
  }

  switch (npc.role) {
    case 'guard':
      return 'guard';
    case 'medic':
      return 'medic';
    case 'technician':
      return 'technician';
    case 'operator':
      return 'operator';
    case 'scientist':
    default:
      return 'civilian';
  }
}

export function resolveNpcFaction(npc: NpcBehaviorSource): MysteryRoomNpcFaction {
  if (npc.faction) {
    return npc.faction;
  }

  const profile = resolveNpcBehaviorProfile(npc);
  if (profile === 'zombie' || profile === 'boss' || npc.actorPreset === 'boss' || npc.actorKind === 'boss') {
    return 'hostile';
  }

  return 'staff';
}

export function resolveNpcRelationshipToHero(npc: NpcBehaviorSource): MysteryRoomNpcRelationshipToHero {
  const faction = resolveNpcFaction(npc);
  if (faction === 'hostile') {
    return 'enemy';
  }
  if (faction === 'ally') {
    return 'ally';
  }
  if (faction === 'staff' && npc.awarenessState === 'alert') {
    return 'enemy';
  }
  return 'neutral';
}

export function npcUsesZombieBehavior(npc: NpcBehaviorSource): boolean {
  return resolveNpcBehaviorProfile(npc) === 'zombie' || npc.actorPreset === 'zombie';
}

export function npcHasSecurityBehavior(npc: NpcBehaviorSource): boolean {
  const profile = resolveNpcBehaviorProfile(npc);
  return profile === 'guard' || profile === 'boss' || npc.role === 'guard';
}

export function npcCanAttackHero(npc: NpcBehaviorSource): boolean {
  if (npc.alive === false) {
    return false;
  }
  const faction = resolveNpcFaction(npc);
  if (faction === 'ally') {
    return false;
  }
  if (npcUsesZombieBehavior(npc)) {
    return true;
  }
  if (faction === 'hostile') {
    return true;
  }
  return faction === 'staff' && npcHasSecurityBehavior(npc) && npc.awarenessState === 'alert';
}

export function npcCanTargetNpc(attacker: NpcBehaviorSource, victim: NpcBehaviorSource): boolean {
  if (attacker.alive === false || victim.alive === false || attacker.id === victim.id) {
    return false;
  }

  const attackerFaction = resolveNpcFaction(attacker);
  const victimFaction = resolveNpcFaction(victim);
  if (attackerFaction === victimFaction) {
    return false;
  }

  if (npcUsesZombieBehavior(attacker)) {
    return victimFaction !== 'hostile';
  }

  return attackerFaction === 'hostile';
}

export function describeNpcBehaviorTree(
  npc: NpcBehaviorSource,
  worldAlarmActive = false,
): MysteryRoomNpcBehaviorTreeNodeId[] {
  if (npc.alive === false) {
    return ['dead'];
  }

  const profile = resolveNpcBehaviorProfile(npc);
  if (profile === 'zombie') {
    return ['hostile_target', 'fallback_wander'];
  }
  if (resolveNpcFaction(npc) === 'hostile') {
    return ['hostile_target', 'security_response', 'fallback_wander'];
  }

  const commonTail: MysteryRoomNpcBehaviorTreeNodeId[] = worldAlarmActive
    ? ['world_alarm', 'routine_rest', 'fallback_wander']
    : ['routine_rest', 'fallback_wander'];

  switch (profile) {
    case 'guard':
    case 'boss':
      return ['security_response', 'routine_patrol', ...commonTail];
    case 'medic':
      return ['medical_support', 'routine_work', ...commonTail];
    case 'technician':
      return ['repair_support', 'routine_work', ...commonTail];
    case 'operator':
    case 'civilian':
    case 'custom':
    default:
      return ['routine_work', ...commonTail];
  }
}
